/**
 * Seite "Liquiditaetsplanung" (Liquiditaetscockpit): USD-Raten offener Bestellungen gegen
 * Kontostand und Kurssicherungen. Neubau ohne Bestandsvorbild; Rechenwege in `lib/liquiditaet.ts`.
 *
 * Die Kopfzeilenfilter (See/Flug/Strasse, EU/China) sind auf dieser Seite ausgeblendet
 * (`Kopfzeile.tsx`); die Seite wendet sie deshalb auch NICHT an - sonst stuende ein unsichtbarer
 * Filter hinter den Zahlen. Sie bekommt entsprechend keine Filterprops.
 *
 * Datenfluss: `bestellungen`/`deals`/`lieferanten` aus dem Store, Kontostand aus der
 * Kurssicherungsuebersicht (`overview.globalCurrent`, geladen von `App.tsx`, solange diese Seite
 * oder die Zahlungsseite offen ist). Drei Filterquellen (Kachel, Woche im Diagramm, Spaltenfilter)
 * wirken auf die Tabelle; Kennzahlen und Diagramm zeigen immer den ungefilterten Plan.
 */
import { useMemo, useState } from "react";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { Ladefehler } from "../Ladefehler/Ladefehler.tsx";
import type { DrawerTabId } from "../Drawer/drawerRechte.ts";
import type { FxOverview } from "../../api/kurssicherung.ts";
import { todayIso } from "../../lib/format.ts";
import type { FxDeal } from "../../lib/fx.ts";
import {
  istKontostandBekannt,
  kontostandVon,
  liquiditaetsKennzahlen,
  liquiditaetsRaten,
  liquiditaetsWochen,
  offeneSicherungen,
  type KachelFilter,
} from "../../lib/liquiditaet.ts";
import type { Order, Supplier } from "../../lib/types.ts";
import { Kennzahlen } from "./Kennzahlen.tsx";
import { leseModus, LiquiditaetsDiagramm, schreibeModus, type DiagrammModus } from "./LiquiditaetsDiagramm.tsx";
import { RatenTabelle } from "./RatenTabelle.tsx";
import { filterChips, LEERER_FILTER, wendeFilterAn, type RatenFilter } from "./filter.ts";

export interface LiquiditaetsSeiteProps {
  bestellungen: readonly Order[];
  deals: readonly FxDeal[] | null | undefined;
  lieferanten: readonly Supplier[] | null | undefined;
  /** Kurssicherungsuebersicht; `null` = noch nicht geladen oder fehlgeschlagen. */
  overview?: Pick<FxOverview, "globalCurrent"> | null;
  /** Der Ladefehler OHNE Stand (`useFxOverview`): die Zeile unten traegt ihn allein, der Hook meldet ihn nicht in den Stapel (L1). */
  overviewLadefehler?: boolean;
  /** Das gefangene Fehlerobjekt hinter `overviewLadefehler`; der Grund der Zeile kommt daraus. */
  overviewLadeursache?: unknown;
  /** Der Neuversuch der Zeile (`useFxOverview#fxOverviewNeuLaden`); ohne ihn gibt es keinen Knopf. Lehnt nie ab. */
  onOverviewNeuLaden?: () => Promise<void> | void;
  /** Oeffnet die Bestellung im Drawer (`App.tsx#drawerOeffnen`). */
  onOeffnen: (bestellungId: string, tab?: DrawerTabId) => void;
  /** ISO-Datum "heute"; nur fuer Tests, sonst `todayIso()`. */
  heute?: string;
}

export function LiquiditaetsSeite({
  bestellungen,
  deals,
  lieferanten,
  overview = null,
  overviewLadefehler = false,
  overviewLadeursache,
  onOverviewNeuLaden,
  onOeffnen,
  heute: heuteVorgabe,
}: LiquiditaetsSeiteProps) {
  const heute = heuteVorgabe ?? todayIso();
  const [modus, setzeModus] = useState<DiagrammModus>(leseModus);
  const [filter, setFilter] = useState<RatenFilter>(LEERER_FILTER);

  const kontostandBekannt = istKontostandBekannt(overview);
  const kontostand = kontostandVon(overview);
  const raten = useMemo(() => liquiditaetsRaten(bestellungen, lieferanten, heute), [bestellungen, lieferanten, heute]);
  const wochen = useMemo(() => liquiditaetsWochen(raten, deals, kontostand, heute), [raten, deals, kontostand, heute]);
  const kennzahlen = useMemo(
    () => liquiditaetsKennzahlen(raten, wochen, deals, kontostand),
    [raten, wochen, deals, kontostand],
  );
  const gefiltert = useMemo(() => wendeFilterAn(raten, filter, wochen, heute), [raten, filter, wochen, heute]);
  const chips = useMemo(() => filterChips(filter, wochen), [filter, wochen]);

  const hatPlanwerte = raten.length > 0 || kontostand !== 0 || offeneSicherungen(deals).length > 0;

  const kachelWaehlen = (kachel: KachelFilter): void =>
    setFilter((alt) => ({ ...alt, kachel: alt.kachel === kachel ? null : kachel }));
  const wocheWaehlen = (key: string): void =>
    setFilter((alt) => ({
      ...alt,
      woche: alt.woche?.key === key ? null : { key, modus },
    }));
  const modusWaehlen = (neu: DiagrammModus): void => {
    setzeModus(neu);
    schreibeModus(neu);
    // Eine gewaehlte Woche gilt je Darstellung anders (nur diese KW / bis einschliesslich): sie folgt dem Umschalter.
    setFilter((alt) => (alt.woche ? { ...alt, woche: { key: alt.woche.key, modus: neu } } : alt));
  };

  return (
    <div className="ov-wrap liq-seite">
      <Seitenkopf titel="Liquiditätsplanung" unterzeile="USD-Raten offener Bestellungen gegen Kontostand und Kurssicherungen · EUR-Bestellungen bleiben unberücksichtigt" />
      {overviewLadefehler ? (
        <Ladefehler
          was="Der USD-Kontostand konnte nicht geladen werden – die Gesichert-Werte zeigen nur offene Sicherungen."
          ursache={overviewLadeursache}
          onErneutLaden={onOverviewNeuLaden}
        />
      ) : null}
      <Kennzahlen
        kennzahlen={kennzahlen}
        kontostandBekannt={kontostandBekannt}
        aktiv={filter.kachel}
        onKachel={kachelWaehlen}
        hatWochen={hatPlanwerte}
      />
      {hatPlanwerte ? (
        <LiquiditaetsDiagramm
          wochen={wochen}
          modus={modus}
          onModus={modusWaehlen}
          auswahl={filter.woche?.key ?? null}
          onWoche={wocheWaehlen}
        />
      ) : (
        <div className="fx-week-chart-card liq-diagramm">
          <div className="fx-week-chart-empty">
            Noch keine USD-Raten, Sicherungen oder kein Kontostand vorhanden - das Diagramm erscheint, sobald Planwerte da sind.
          </div>
        </div>
      )}
      <RatenTabelle
        alle={raten}
        gefiltert={gefiltert}
        filter={filter}
        onFilter={setFilter}
        chips={chips}
        onOeffnen={onOeffnen}
      />
    </div>
  );
}
